// A component's own limitations — the pure half of the Aircraft Item Details tab's
// add / edit (component editor, 2026-10-07). The API is POST / PATCH / DELETE
// /v6/items/{id}/limitations (app/items/limitations.py).
//
// Legacy (intent): mod_parts/includes/tabs/parts_tab_item_aircraft.inc:598-660 posts
// save_limitation (parts_ajax_handler.inc:2090-2229).
//   @Install TSN -> total_life_used (TSN AT INSTALL; "UNK" = -1, :620)
//   @Install TSO -> life_at_install
// The Total TSN / TSO the grid shows are computed by the API and are never sent back.

/** A row of GET /v6/items/{id}/limitations (app/items/schemas.py ItemLimitationList). */
export interface ComponentLimitationRow {
  id: number;
  item_id: number;
  based_on: string | null;
  limitation_type: string | null;
  life: string | number | null;
  life_hours: string | number | null;
  warning: string | number | null;
  warning_unit: string | null;
  total_life_used: string | number | null;
  life_at_install: string | number | null;
  aircraft_install: string | number | null;
  aircraft_id: number | null;
  position: string | null;
  install_date: string | null;
  expiry_date: string | null;
  limitation_notes: string | null;
  units_remaining: string | number | null;
  used_this_install: string | number | null;
  total_tsn: string | number | null;
  total_tso: string | number | null;
  next_due: string | null;
  remaining_units: string | number | null;
  remaining_days: number | null;
}

export interface LimitationFormValues {
  based_on: string;
  limitation_type: string;
  life: string;
  life_hours: string;
  warning: string;
  warning_unit: string;
  tsn: string;
  tsn_unknown: boolean;
  tso: string;
  aircraft_install: string;
  /** "YYYY-MM-DD" or "" (TenantDatePicker's wire form). */
  install_date: string;
  expiry_date: string;
  limitation_notes: string;
  time_to_run: string;
}

/** Legacy's default Type list when the tenant sets no aircraft_component_type_list
 * (parts_tab_item_aircraft.inc:12-19). */
export const DEFAULT_COMPONENT_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Overhaul", label: "Overhaul" },
  { value: "Limited Life", label: "Limited Life" },
  { value: "On Condition", label: "On Condition" },
  { value: "Not Applicable", label: "Not Applicable" },
  { value: "Retire", label: "Retire" },
  { value: "Servicing", label: "Servicing" },
];

/** aircraft_component_type_list is "value:name,value:name" (parts_tab_item_aircraft.inc:4-10);
 * a bare "value" is its own label. Blank or missing → legacy's defaults. */
export function componentTypeOptions(raw?: string | null): Array<{ value: string; label: string }> {
  const parsed = (raw ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [value, ...rest] = part.split(":");
      const label = rest.join(":").trim();
      return { value: value.trim(), label: label || value.trim() };
    })
    .filter((o) => o.value);
  return parsed.length ? parsed : [...DEFAULT_COMPONENT_TYPES];
}

export const isDateAxis = (basedOn?: string | null) => (basedOn ?? "").includes("Date");
export const isConditionAxis = (basedOn?: string | null) =>
  ["On Condition", "Not Applicable"].includes((basedOn ?? "").trim());

const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
/** The wire's "YYYY-MM-DD" day, "" for none or legacy's zero-date. */
const isoDay = (raw?: string | null) => {
  const day = String(raw ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !day.startsWith("0000") ? day : "";
};

export function emptyLimitationForm(): LimitationFormValues {
  return {
    based_on: "",
    limitation_type: "",
    life: "",
    life_hours: "",
    warning: "",
    warning_unit: "",
    tsn: "",
    tsn_unknown: false,
    tso: "",
    aircraft_install: "",
    install_date: "",
    expiry_date: "",
    limitation_notes: "",
    time_to_run: "",
  };
}

/** The edit form seeded from a row. TSN is the AT-INSTALL figure (total_life_used), never
 * the computed Total TSN — seeding the total is how a re-save double-counts. */
export function limitationFormValues(row: ComponentLimitationRow): LimitationFormValues {
  const unknown = Number(row.total_life_used) === -1;
  return {
    based_on: str(row.based_on),
    limitation_type: str(row.limitation_type),
    life: str(row.life),
    life_hours: str(row.life_hours),
    warning: str(row.warning),
    warning_unit: str(row.warning_unit),
    tsn: unknown ? "" : str(row.total_life_used),
    tsn_unknown: unknown,
    tso: str(row.life_at_install),
    aircraft_install: str(row.aircraft_install),
    install_date: isoDay(row.install_date),
    expiry_date: isoDay(row.expiry_date),
    limitation_notes: str(row.limitation_notes),
    time_to_run: str(row.units_remaining),
  };
}

const numOrNull = (v: string) => (v.trim() === "" ? null : v.trim());

/** The POST / PATCH body. Off an aircraft the install fields are not sent (the API ignores
 * them there anyway); a blank Due Off on the date axis lets the API compute legacy's. */
export function limitationPayload(values: LimitationFormValues, fitted: boolean): Record<string, unknown> {
  const body: Record<string, unknown> = {
    based_on: values.based_on.trim(),
    limitation_type: values.limitation_type || null,
    life: numOrNull(values.life) ?? "0",
    life_hours: numOrNull(values.life_hours) ?? "0",
    warning: numOrNull(values.warning) ?? "0",
    warning_unit: values.warning_unit || null,
    tso: numOrNull(values.tso) ?? "0",
    limitation_notes: values.limitation_notes || null,
  };
  if (values.tsn_unknown) body.tsn_unknown = true;
  else body.tsn = numOrNull(values.tsn) ?? "0";
  if (isDateAxis(values.based_on)) body.expiry_date = values.expiry_date || null;
  if (fitted) {
    body.install_date = values.install_date || null;
    if (!isDateAxis(values.based_on)) body.aircraft_install = numOrNull(values.aircraft_install);
  } else if (numOrNull(values.time_to_run) !== null) {
    body.time_to_run = values.time_to_run.trim();
  }
  return body;
}

/** The form's own checks, before a request: what the API would refuse with a 422. */
export function limitationFormErrors(values: LimitationFormValues, fitted: boolean): Partial<Record<keyof LimitationFormValues, string>> {
  const errors: Partial<Record<keyof LimitationFormValues, string>> = {};
  if (!values.based_on.trim()) errors.based_on = "Based On is required";
  if (fitted && !values.install_date) errors.install_date = "Date On is required";
  if (fitted && !isDateAxis(values.based_on) && !values.aircraft_install.trim()) {
    errors.aircraft_install = "@A/C Unit Fitted is required";
  }
  return errors;
}
